import { useUserStore } from "@/auth/stores/user";
import { colors, spacing } from "@/config/theme";
import useBoolean from "@/hooks/useBoolean";
import { useCheckReportUpdatedTime } from "@/reports/api";
import { Location } from "@/reports/config/locations";
import { ReportType } from "@/reports/types/Api";
import styled from "@emotion/styled";
import { Modal, Space, Tag } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const OptionWrapper = styled.div<{
  recommended: boolean;
}>`
  padding: ${spacing.xs};
  border-radius: 4px;
  border: 1px solid
    ${(props) =>
      props.recommended ? colors.daybreakBlue[300] : colors.gray[500]};
  background-color: ${(props) =>
    props.recommended ? colors.daybreakBlue[50] : colors.gray[450]};
`;

const OptionItem = ({
  title,
  description,
  recommended = false,
}: {
  title: string;
  description: string;
  recommended?: boolean;
}) => {
  return (
    <OptionWrapper recommended={recommended}>
      <Space>
        <b>{title}</b>
        {recommended && <Tag color="processing">Recommended</Tag>}
      </Space>
      <p>{description}</p>
    </OptionWrapper>
  );
};

const SimultaneousUsageWarningModal = ({
  reportType,
  showSaveOverwriteAlert,
}: {
  reportType: ReportType;
  showSaveOverwriteAlert: () => void;
}) => {
  const { user } = useUserStore();
  const { location, id } = useParams();

  const { value: showWarningModal, setValue: setShowWarningModal } =
    useBoolean(false);

  const { data } = useCheckReportUpdatedTime({
    location: location as Location,
    id,
    reportType,
    enabled: !showWarningModal, // Disable the query when the warning modal is showing to avoid unnecessary API calls
  });

  const [cachedUpdatedTime, setCachedUpdatedTime] = useState<{
    updatedAt: string | null;
    updatedBy: number | null;
  } | null>(null);

  useEffect(
    () => {
      if (data) {
        // Cache the updated time and updated by user id when first time fetch the data
        if (!cachedUpdatedTime) {
          setCachedUpdatedTime({
            updatedAt: data.updatedAt,
            updatedBy: data.updatedBy,
          });
        } else if (
          dayjs(data.updatedAt).isAfter(dayjs(cachedUpdatedTime.updatedAt)) &&
          data.updatedBy !== user?.UserId
        ) {
          // Show warning modal if the report has been updated by other user since last time cached
          setShowWarningModal(true);
          // Update the cached updated time to the latest value so that the modal will not keep popping up
          setCachedUpdatedTime({
            updatedAt: data.updatedAt,
            updatedBy: data.updatedBy,
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  return (
    <Modal
      open={showWarningModal}
      closable={false}
      maskClosable={false}
      title="Changes detected"
      cancelText="Ignore and Continue"
      okText="Refresh Report Tab"
      onOk={() => {
        // Reload page
        window.location.reload();
      }}
      onCancel={() => {
        showSaveOverwriteAlert();
        setShowWarningModal(false);
      }}
    >
      <Space
        direction="vertical"
        style={{
          marginBottom: 12,
        }}
      >
        <p>
          Another user has updated <u>this report tab</u> since your last save.
          Saving now or switching tabs will overwrite their changes. Please
          choose how you would like to proceed:
        </p>

        <OptionItem
          title="Refresh Report Tab"
          description="This action will load the latest version. Your unsaved changes will be lost. Recommended to avoid overwriting other user's edits."
          recommended
        />
        <OptionItem
          title="Ignore and Continue Editing"
          description="Keep your current edits. When you save or switch report tabs, you will overwrite the other user's changes. Use this with caution."
        />
      </Space>
    </Modal>
  );
};

export default SimultaneousUsageWarningModal;
